"use client";

import React, { useState, useMemo, useRef, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Clock, Share2, Sparkles, ExternalLink, ArrowRight, Check, RotateCcw, BookOpen } from "lucide-react";
import { Reveal } from "@/components/cosmic/Reveal";
import { focusWithoutScroll } from "@/lib/scroll";
import styles from "../CosmicDiscoveries.module.css";
export interface ArticleItem {
  id: string;
  category: string;
  readTime: string;
  title: string;
  excerpt: string;
  citation: string;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  fullText: string[];
  relatedTopics: string[];
}
export const STARTER_ARTICLES: ArticleItem[] = [{
  id: "art-1",
  category: "Observational Cosmology",
  readTime: "8 min read",
  title: "James Webb Space Telescope Challenges Early Galaxy Formation Models",
  excerpt: "Massive luminous galaxies detected within 400 million years of the Big Bang force astrophysicists to revisit cosmic dawn growth models.",
  citation: "Sources: Astrophysical Journal Letters / STScI",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/jwst_deep_field_early_galaxies.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/jwst_deep_field_early_galaxies.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/jwst_deep_field_early_galaxies.png-wvc-sizes",
    alt: "Deep-space telescope image showing ancient red-shifted galaxies"
  },
  fullText: ["Recent NIRCam and NIRSpec spectroscopic surveys from the James Webb Space Telescope have revealed an unexpectedly dense population of luminous galaxy candidates at redshifts z > 10. These cosmic structures, observed less than 400 million years after the initial expansion, exhibit stellar masses and star-formation rates orders of magnitude higher than canonical Lambda-CDM halo accretion limits predicted.", "Astrophysicists are evaluating whether non-standard primordial perturbation spectra, enhanced star-formation efficiency within early molecular clouds, or top-heavy initial mass functions (Pop III stars) can account for this observed excess luminosity without overturning fundamental cosmological baselines."],
  relatedTopics: ["Cosmic Dawn Reionization", "Early Supermassive Seed Black Holes", "NIRCam Spectral Analysis"]
}, {
  id: "art-2",
  category: "Quantum Mechanics",
  readTime: "6 min read",
  title: "Closing the Cosmic Bell Test Loophole with Ancient Quasar Photons",
  excerpt: "Using 12-billion-year-old light to dictate measurement settings confirms quantum entanglement correlations with zero local hidden variables.",
  citation: "Sources: Physical Review Letters / MIT Physics",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/quantum_quasar_bell_experiment.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/quantum_quasar_bell_experiment.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/quantum_quasar_bell_experiment.webp-wvc-sizes",
    alt: "Astrophysical visualization of high-energy photon beams emitted from distant quasars"
  },
  fullText: ["By utilizing high-redshift light emitted by two separate quasars over 7.8 and 12.2 billion years ago to randomly determine polarizer basis settings at the Roque de los Muchachos Observatory, experimentalists pushed the 'freedom-of-choice' loophole back to cosmic infancy.", "The statistical measurements demonstrated violations of Bell inequalities by more than 9 standard deviations. Any alternative mechanism governed by classical local realism would have required coordination set in motion prior to the emission of both quasar wave-packets, strengthening quantum nonlocality as a baseline universal law."],
  relatedTopics: ["Quantum Nonlocality", "Astronomical Setting Generators", "EPR Paradox Telemetry"]
}, {
  id: "art-3",
  category: "Multiverse Theory",
  readTime: "11 min read",
  title: "Vacuum Decay and the Stability of the Standard Model Higgs Field",
  excerpt: "Precise measurements of the top quark mass imply our universe's vacuum state may be metastable, bearing implications for eternal inflation bubble walls.",
  citation: "Sources: CERN High Energy Physics / Physical Review D",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/higgs_field_vacuum_decay_multiverse.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/higgs_field_vacuum_decay_multiverse.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1830392/img/higgs_field_vacuum_decay_multiverse.png-wvc-sizes",
    alt: "Topological bubble nucleation visualization within an inflating cosmic manifold"
  },
  fullText: ["Extrapolations of Standard Model renormalization group equations using the latest ATLAS and CMS top-quark mass bounds (172.5 GeV) place the electroweak Higgs potential in a narrow metastable corridor. While the expected decay lifetime via quantum tunneling vastly exceeds the current age of the cosmos, localized gravitational fluctuations or ultra-high-energy primordial collisions could trigger nucleation.", "Within modern eternal inflation frameworks, such phase transitions serve as the boundary generators between adjacent pocket universes, with true-vacuum bubbles expanding outward at nearly the speed of light to sculpt heterogeneous local physics across the multiverse."],
  relatedTopics: ["False Vacuum Nucleation", "Eternal Cosmic Inflation", "Electroweak Scale Tuning"]
}];
interface ArticlesContentProps {
  searchQuery: string;
  activeCategory: string;
  onResetFilters: () => void;
}
function ArticlesContent({
  searchQuery,
  activeCategory,
  onResetFilters
}: ArticlesContentProps) {
  const {
    posts
  } = usePosts();
  const [selectedArticle, setSelectedArticle] = useState<ArticleItem | null>(null);
  const openerButtonRef = useRef<HTMLElement | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const dialogScrollRef = useRef<HTMLDivElement | null>(null);

  // If WordPress has published articles, synthesize them with starter metadata; otherwise display starter items
  const baseArticles: ArticleItem[] = useMemo(() => {
    if (posts && posts.length > 0) {
      return posts.map((post, idx) => {
        const fallback = STARTER_ARTICLES[idx % STARTER_ARTICLES.length];
        // Parse paragraphs from post.content if available
        let contentParagraphs: string[] = fallback.fullText;
        if (post.content && post.content.trim()) {
          const rawParagraphs = post.content.split(/<\/p>|<br\s*\/?>/i).map(p => p.replace(/<[^>]+>/g, "").trim()).filter(p => p.length > 10);
          if (rawParagraphs.length > 0) {
            contentParagraphs = rawParagraphs;
          }
        }
        return {
          id: post.id || fallback.id,
          category: post.categories?.[0]?.name || fallback.category,
          readTime: fallback.readTime,
          title: post.title || fallback.title,
          excerpt: post.excerpt ? post.excerpt.replace(/<[^>]+>/g, "").slice(0, 200) : fallback.excerpt,
          citation: fallback.citation,
          image: post.featuredImage ? {
            src: post.featuredImage,
            srcset: "",
            sizes: "",
            alt: post.title || fallback.image.alt
          } : fallback.image,
          fullText: contentParagraphs,
          relatedTopics: fallback.relatedTopics
        };
      });
    }
    return STARTER_ARTICLES;
  }, [posts]);

  // Filter based on search query and active category
  const filteredArticles = useMemo(() => {
    return baseArticles.filter(article => {
      // Category match
      if (activeCategory !== "all") {
        const cat = article.category.toLowerCase();
        if (activeCategory === "astrophysics" && !cat.includes("astrophys") && !cat.includes("cosmology") && !cat.includes("space")) {
          return false;
        }
        if (activeCategory === "quantum" && !cat.includes("quantum")) {
          return false;
        }
        if (activeCategory === "multiverse" && !cat.includes("multiverse")) {
          return false;
        }
        if (activeCategory === "cosmology" && !cat.includes("cosmology")) {
          return false;
        }
      }

      // Search match
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const inTitle = article.title.toLowerCase().includes(q);
        const inExcerpt = article.excerpt.toLowerCase().includes(q);
        const inCategory = article.category.toLowerCase().includes(q);
        const inCitation = article.citation.toLowerCase().includes(q);
        const inTopics = article.relatedTopics.some(t => t.toLowerCase().includes(q));
        const inText = article.fullText.some(p => p.toLowerCase().includes(q));
        if (!inTitle && !inExcerpt && !inCategory && !inCitation && !inTopics && !inText) {
          return false;
        }
      }
      return true;
    });
  }, [baseArticles, activeCategory, searchQuery]);
  const handleShare = async () => {
    if (typeof window !== "undefined" && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2400);
      } catch {
        // Fallback silently if clipboard permissions are restricted
      }
    }
  };
  const handleDialogScroll = () => {
    if (!dialogScrollRef.current) return;
    const {
      scrollTop,
      scrollHeight,
      clientHeight
    } = dialogScrollRef.current;
    const total = scrollHeight - clientHeight;
    if (total <= 0) {
      setScrollProgress(100);
    } else {
      setScrollProgress(Math.min(100, Math.max(0, Math.round(scrollTop / total * 100))));
    }
  };
  const featuredArticle = filteredArticles[0];
  const secondaryArticles = filteredArticles.slice(1);
  return <div className="mx-auto w-full max-w-7xl">
      {/* Result Count and Active Filters Bar */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-4 sm:mb-8 sm:gap-4">
        <div className="flex flex-wrap items-center gap-2 font-mono text-xs text-muted-foreground">
          <span className="size-1.5 shrink-0 rounded-full bg-primary" />
          <span className="break-words">
            Telemetry Feed: <strong className="text-foreground">{filteredArticles.length}</strong> {filteredArticles.length === 1 ? "dispatch" : "dispatches"} synchronized
          </span>
          {(activeCategory !== "all" || searchQuery) && <span className="text-primary font-medium">
              (filtered)
            </span>}
        </div>

        {(activeCategory !== "all" || searchQuery) && <Button type="button" variant="ghost" size="sm" onClick={onResetFilters} className="h-auto min-h-[44px] gap-1.5 rounded-full font-mono text-xs text-muted-foreground hover:text-primary sm:h-7 sm:min-h-[36px]">
            <RotateCcw className="size-3" />
            <span>Reset filters</span>
          </Button>}
      </div>

      {/* Empty State */}
      {filteredArticles.length === 0 && <div className="ql-glass my-8 rounded-xl p-6 text-center sm:my-12 sm:p-12">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full border border-border bg-muted/40 sm:size-14">
            <BookOpen className="size-5 text-primary sm:size-6" />
          </div>
          <h3 className="mt-4 font-serif text-lg font-bold text-foreground sm:text-xl">
            No Dispatches Matched Telemetry
          </h3>
          <p className="mx-auto mt-2 max-w-md font-sans text-xs leading-relaxed text-muted-foreground sm:text-sm">
            No research papers matched your search query or category filter. Adjust your terms or reset the observatory filters.
          </p>
          <div className="mt-6 flex justify-center">
            <Button type="button" onClick={onResetFilters} className="ql-btn min-h-[44px] text-xs">
              Reset Filters
            </Button>
          </div>
        </div>}

      {/* Magazine Composition: 1 Large Featured Dispatch + Column/Grid of Secondary Dispatches */}
      {filteredArticles.length > 0 && <div className="space-y-6 sm:space-y-8">
          {/* Featured Hero Card */}
          {featuredArticle && <article className="ql-card group rounded-xl border border-border bg-card p-0 transition-all duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                {/* Large Featured Image Frame */}
                <div className="relative aspect-16/10 w-full overflow-hidden border-b border-border bg-muted lg:col-span-7 lg:border-b-0 lg:border-r">
                  <img src={featuredArticle.image.src} data-wvc-srcset={featuredArticle.image.srcset} data-wvc-sizes={featuredArticle.image.sizes} alt={featuredArticle.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/25 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-background/10 lg:to-background/80" />
                  <div className="absolute top-3 left-3 flex flex-wrap items-center gap-1.5 sm:top-4 sm:left-4 sm:gap-2">
                    <Badge variant="outline" className="border-primary/50 bg-background/85 font-mono text-[10px] uppercase tracking-wider text-primary backdrop-blur-md sm:text-xs">
                      Featured Research
                    </Badge>
                    <Badge variant="outline" className="max-w-[200px] truncate border-border bg-background/80 font-mono text-[10px] text-muted-foreground backdrop-blur-md sm:max-w-none sm:text-xs">
                      {featuredArticle.category}
                    </Badge>
                  </div>
                  <div className="absolute right-3 bottom-3 flex items-center gap-1.5 rounded-full bg-background/85 px-2.5 py-0.5 font-mono text-[10px] text-muted-foreground backdrop-blur-md sm:right-4 sm:bottom-4 sm:px-3 sm:py-1 sm:text-xs">
                    <Clock className="size-3 text-primary sm:size-3.5" />
                    <span>{featuredArticle.readTime}</span>
                  </div>
                </div>

                {/* Content Column */}
                <div className="flex flex-col justify-between p-5 sm:p-8 lg:col-span-5">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-2 font-mono text-xs text-primary">
                      <Sparkles className="size-3.5 shrink-0" />
                      <span className="uppercase tracking-wider">Lead Dispatch</span>
                    </div>

                    <h3 className="font-serif text-lg font-bold leading-snug tracking-tight text-foreground break-words transition-colors group-hover:text-primary sm:text-2xl lg:text-3xl">
                      {featuredArticle.title}
                    </h3>

                    <p className="font-sans text-sm leading-relaxed text-muted-foreground break-words sm:text-base">
                      {featuredArticle.excerpt}
                    </p>

                    {/* ql-card-reveal for citation */}
                    <div className="ql-card-reveal border-t border-border/60 pt-3">
                      <p className="font-mono text-xs text-muted-foreground break-words">
                        {featuredArticle.citation}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {featuredArticle.relatedTopics.map((topic, i) => <span key={i} className="rounded-full border border-border/80 bg-muted/40 px-2 py-0.5 font-mono text-[10px] text-muted-foreground">
                            {topic}
                          </span>)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 border-t border-border/60 pt-4 sm:mt-6">
                    <Button type="button" onClick={e => {
                openerButtonRef.current = e.currentTarget;
                setSelectedArticle(featuredArticle);
              }} className="ql-btn w-full min-h-[44px] sm:w-auto">
                      <span>Read Full Dispatch</span>
                      <ArrowRight className="size-4 shrink-0" />
                    </Button>
                  </div>
                </div>
              </div>
            </article>}

          {/* Secondary Cards Grid */}
          {secondaryArticles.length > 0 && <div className="grid grid-cols-1 gap-5 sm:gap-6 md:grid-cols-2">
              {secondaryArticles.map(article => <article key={article.id} className="ql-card group flex flex-col justify-between rounded-xl border border-border bg-card p-0 transition-all duration-300">
                  {/* Image Frame */}
                  <div className="relative aspect-16/9 w-full overflow-hidden border-b border-border bg-muted">
                    <img src={article.image.src} data-wvc-srcset={article.image.srcset} data-wvc-sizes={article.image.sizes} alt={article.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent" />
                    <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3">
                      <Badge variant="outline" className="max-w-[200px] truncate border-primary/40 bg-background/80 font-mono text-[0.65rem] uppercase tracking-wider text-primary backdrop-blur-xs sm:max-w-none sm:text-[0.7rem]">
                        {article.category}
                      </Badge>
                    </div>
                    <div className="absolute right-2.5 bottom-2.5 flex items-center gap-1 rounded-full bg-background/85 px-2 py-0.5 font-mono text-[10px] text-muted-foreground backdrop-blur-xs sm:right-3 sm:bottom-3 sm:gap-1.5 sm:px-2.5 sm:py-1 sm:text-xs">
                      <Clock className="size-3 shrink-0 text-primary" />
                      <span>{article.readTime}</span>
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="flex flex-1 flex-col justify-between p-4 sm:p-6">
                    <div className="space-y-2.5 sm:space-y-3">
                      <h3 className="font-serif text-base font-bold leading-snug tracking-tight text-foreground break-words transition-colors group-hover:text-primary sm:text-lg">
                        {article.title}
                      </h3>
                      <p className="line-clamp-3 font-sans text-xs leading-relaxed text-muted-foreground break-words sm:text-sm">
                        {article.excerpt}
                      </p>

                      {/* Reveal Citation */}
                      <div className="ql-card-reveal border-t border-border/60 pt-3">
                        <p className="font-mono text-xs text-muted-foreground/90 break-words">
                          {article.citation}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 pt-2 sm:mt-6">
                      <button type="button" onClick={e => {
                openerButtonRef.current = e.currentTarget;
                setSelectedArticle(article);
              }} className="inline-flex min-h-[44px] w-full items-center justify-between rounded-full border border-border bg-muted/40 px-4 py-2 font-mono text-xs text-foreground transition-colors hover:border-primary hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                        <span>Read Full Dispatch</span>
                        <ArrowRight className="size-3.5 shrink-0" />
                      </button>
                    </div>
                  </div>
                </article>)}
            </div>}
        </div>}

      {/* Full Article Reading Dialog Styled as a Premium Reader */}
      <Dialog open={!!selectedArticle} onOpenChange={open => {
      if (!open) {
        setSelectedArticle(null);
        setScrollProgress(0);
      }
    }}>
        {selectedArticle && <DialogContent onCloseAutoFocus={e => {
        // Prevent Radix default window jump on focus return
        e.preventDefault();
        if (openerButtonRef.current) {
          focusWithoutScroll(openerButtonRef.current);
        }
      }} className={`bg-popover ql-glass-strong ${styles.readerDialog} w-[calc(100vw-1.5rem)] max-w-3xl overflow-hidden border-border p-0 text-card-foreground sm:w-full`}>
            {/* Top Reading Progress Bar */}
            <div className="relative h-1 w-full bg-border/40">
              <div className={`h-full bg-primary ${styles.readingProgressBar}`} style={{
            width: `${scrollProgress}%`
          }} aria-label={`Reading progress: ${scrollProgress}%`} />
            </div>

            <div ref={dialogScrollRef} onScroll={handleDialogScroll} className={`${styles.readerScrollArea} overflow-y-auto p-4 sm:p-10`}>
              <DialogHeader className="gap-2.5 border-b border-border/80 pb-5 text-left sm:gap-3 sm:pb-6">
                <div className="flex flex-wrap items-center justify-between gap-2.5 sm:gap-3">
                  <Badge variant="outline" className="border-primary/50 bg-primary/10 font-mono text-[10px] tracking-wider text-primary sm:text-xs">
                    {selectedArticle.category}
                  </Badge>
                  <div className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground">
                    <Clock className="size-3.5 text-primary" />
                    <span>{selectedArticle.readTime}</span>
                  </div>
                </div>

                <DialogTitle className="font-serif text-xl font-bold leading-tight text-foreground break-words sm:text-3xl">
                  {selectedArticle.title}
                </DialogTitle>

                <DialogDescription className="font-mono text-xs text-muted-foreground break-words">
                  {selectedArticle.citation}
                </DialogDescription>
              </DialogHeader>

              {/* Reader Body with max-width ~65ch for premium typography */}
              <div className="space-y-5 pt-5 sm:space-y-6 sm:pt-6">
                <div className="relative aspect-16/10 w-full overflow-hidden rounded-xl border border-border bg-muted">
                  <img src={selectedArticle.image.src} data-wvc-srcset={selectedArticle.image.srcset} data-wvc-sizes={selectedArticle.image.sizes} alt={selectedArticle.image.alt} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>

                {/* Prose column with drop-cap first paragraph */}
                <div className="mx-auto max-w-[65ch] space-y-4 text-sm leading-relaxed text-foreground/90 break-words sm:space-y-5 sm:text-lg sm:leading-8">
                  {selectedArticle.fullText.map((paragraph, idx) => <p key={idx} className={idx === 0 ? styles.dropCap : undefined}>
                      {paragraph}
                    </p>)}
                </div>

                {/* Verification & Provenance Block */}
                <div className="mx-auto max-w-[65ch] rounded-xl border border-border/80 bg-muted/40 p-4 sm:p-5">
                  <div className="flex items-center gap-2 font-serif text-xs font-semibold uppercase tracking-wider text-primary">
                    <ExternalLink className="size-3.5 shrink-0" />
                    <span>Verification & Provenance</span>
                  </div>
                  <p className="mt-1.5 font-mono text-xs leading-relaxed text-muted-foreground break-words">
                    {selectedArticle.citation}. Pre-print manuscripts cross-verified across arXiv astrophysical archives and primary observatory logs.
                  </p>
                </div>

                {/* Related Research Topics Chips */}
                <div className="mx-auto max-w-[65ch] space-y-2.5 border-t border-border pt-4 sm:pt-5">
                  <span className="font-mono text-xs font-semibold uppercase tracking-wider text-primary">
                    Related Research Areas
                  </span>
                  <div className="flex flex-wrap gap-1.5 pt-1 sm:gap-2">
                    {selectedArticle.relatedTopics.map((topic, idx) => <span key={idx} className="ql-chip text-xs">
                        {topic}
                      </span>)}
                  </div>
                </div>

                {/* Actions Footer */}
                <div className="mx-auto flex max-w-[65ch] flex-wrap items-center justify-between gap-3 border-t border-border pt-5 sm:gap-4 sm:pt-6">
                  <Button type="button" variant="outline" size="sm" onClick={handleShare} className="min-h-[44px] rounded-full border-border font-mono text-xs text-foreground transition-colors hover:border-primary hover:text-primary sm:min-h-[36px]">
                    {copiedLink ? <>
                        <Check className="size-3.5 text-primary" />
                        <span>URL Copied to Clipboard</span>
                      </> : <>
                        <Share2 className="size-3.5" />
                        <span>Share Dispatch</span>
                      </>}
                  </Button>

                  <Button type="button" onClick={() => setSelectedArticle(null)} className="ql-btn min-h-[44px] text-xs">
                    Close Document
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>}
      </Dialog>
    </div>;
}
export interface ArticleGridProps {
  searchQuery?: string;
  activeCategory?: string;
  onResetFilters?: () => void;
}
export default function ArticleGrid({
  searchQuery = "",
  activeCategory = "all",
  onResetFilters = () => {}
}: ArticleGridProps) {
  return <section id="article-grid" data-nav="dark" className="relative w-full max-w-full overflow-hidden py-10 text-foreground sm:py-16 md:py-24 isolate">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal delay={60}>
          {/* Section Header */}
          <div className="mb-8 text-center sm:mb-10 md:mb-14">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1 text-xs font-medium text-primary">
              <Sparkles className="size-3.5 shrink-0" />
              <span className="font-mono tracking-widest uppercase">Verified Research</span>
            </div>

            <h2 className="mt-3 font-serif text-2xl font-extrabold tracking-tight sm:mt-4 sm:text-4xl lg:text-5xl text-foreground break-words">
              <span className="ql-title">Frontier Astrophysics & Quantum Research</span>
            </h2>

            <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground break-words sm:mt-4 sm:text-base md:text-lg">
              Peer-reviewed explorations into cosmic structure formation, vacuum phase transitions, and quantum measurement anomalies.
            </p>
          </div>
        </Reveal>

        {/* Dynamic / Starter Posts Consumer */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <ArticlesContent searchQuery={searchQuery} activeCategory={activeCategory} onResetFilters={onResetFilters} />
        </PostsProvider>
      </div>
    </section>;
}